/**
 * 🔴 **长页面吸顶页内导航** —— `AC-O2.36`（O 域 PRD · 2026-09-27 增补）在客户端的**唯一实现**。
 *
 * ## 依据（三处交叉验证，口径完全一致）
 *
 * | 来源 | 内容 |
 * |---|---|
 * | O 域 PRD `AC-O2.36` | 滚动高度 **≥ 1.5 屏**的内容页，须在内容区顶部提供**吸顶锚点条**（钉在顶栏下方）；**单一实现**，**禁每页各写一份**；短页不出现；锚点文案优先取 `data-anchor`，其次块内标题，**无标题则不产出** |
 * | UI 移交文档 §8 | 「实现落点 = 共享层（React 侧对应**一个 `<AnchorBar>` 组件**），禁每页各写一份」 |
 * | 原型 `_chrome.js` `buildAnchorBar()` + `app.css` `.anchor-bar` | 移植源（本文件逐条对照实现） |
 *
 * ## 🔴 为什么"单一实现"是硬要求而不是风格偏好
 *
 * 原型的做法是**页面零配置**：`initPage()` 里调一次 `buildAnchorBar()`，
 * 所有长页面自动获得导航条。若 React 侧改成"每页自己写一个锚点列表"，
 * 立刻出现 5 个后果：① 阈值（1.5 屏 / 块高 240）会在 9 个页面里各有一份；
 * ② 有的页面忘了加 ⇒ **静默缺失**（没有任何报错）；③ 文案截断规则漂移；
 * ④ 滚动高亮逻辑重写 9 次；⑤ 设计一改就要改 9 处。⇒ 与 `Grid.tsx`、`SearchSelect.tsx` 同理，
 * **规则只有一份，页面只负责"摆区块"**。
 *
 * ## 🔴 与原型的关键差异：什么时候能测（**这是本组件最要紧的一段**）
 *
 * 原型的阈值判定（`scrollHeight / innerHeight ≥ 1.5`）**必须在浏览器里做** ——
 * 而本项目的端侧测试基座是 `renderToStaticMarkup`（**零新依赖**，无 DOM、不执行 effect，
 * 见 `test/render.tsx`）。若把全部逻辑写进 effect，本组件的规则在单测里**一行都测不到**，
 * 只能靠"看起来对"。⇒ 本批把规则**切成两半**：
 *
 * ```
 *                  ┌──────────── 纯逻辑（可直测，零 DOM） ────────────┐
 *   元素候选集 ──▶ │ 测量结果(矮高/标题/顺序) ──▶ pickAnchors() ──▶ 锚点清单 │
 *                  └───────────────────────────────────────────────────┘
 *                                                                  │
 *   真实 DOM 测量 ──────────────────────────────────────────────▶ 渲染 + 滚动同步
 * ```
 *
 * - `pickAnchors()` 接收**已经量好的数字**（`height` / `innerHeights` / `hasDataAnchor`）
 *   与**元素本身的顺序**，返回锚点清单与 `null`（= 不产出）。它是纯函数，**不需要浏览器**。
 * - `measureBlock()` / `collectBlocks()` 是薄薄的 DOM 适配层（唯一碰 `getBoundingClientRect` 的地方）。
 * - 组件在 `useEffect` 里做真实测量 —— 而 `useEffect` 在 SSR 不执行 ⇒
 *   **静态渲染时组件返回 `null`**，这是**正确的**（短页与"还没测"都该不显示）。
 *
 * 🔴 **后果必须说清**：端侧单测**证不了**"某页面在浏览器里真的出现了吸顶条"。
 * 那属于 `test/render.tsx` 升级触发线 ①/②（真实交互 / 事件后状态）——
 * 由**无头浏览器几何取证**负责（读 `.anchor-bar` 是否存在 + `position: sticky` + 跳转后 `scrollY`）。
 * 本 spec 证的是「**给同样的测量结果，规则输出对不对**」。
 *
 * ## 阈值（照抄原型，勿"优化"）
 *
 * | 常量 | 值 | 含义 |
 * |---|---|---|
 * | `MIN_BLOCK` | 240 | 块高下限：太矮不值得做锚点（**显式 `data-anchor` 不受此限**） |
 * | `MIN_RATIO` | 1.5 | 页面/视口 比下限：不到 1.5 屏不算"长页面"（`AC-O2.36` 原文） |
 * | `MIN_CHILD` | 200 | 下探时"大子块"的门槛 |
 * | `DEEP_MAX` | 6 | 兜底探测最多取几个卡片 |
 * | `LABEL_MAX` | 14 | 文案截断（原型 `slice(0, 14)`） |
 *
 * ## 锚点 id 的生成（**不改用户页面结构**）
 *
 * 原型直接改 `el.id = 'jy-a1'`。React 侧同样在 effect 里做 —— 但**只给还没有 id 的块加**，
 * 且前缀 `jy-a` 与原型一致（便于两套实现对照，也让"这个 id 是谁加的"一眼可辨）。
 *
 * ## ⚠️ 本组件**不做**什么（诚实登记）
 *
 * - **不参与导航**：原型 `initPage` 里还有"数字键导航"等 —— 那是 `AC-O2.8`，另一件事。
 * - **不自算区块内容**：它只读 DOM（标题 / 高度 / 顺序），不接收"页面告诉我有哪些区块"
 *   这样的 props。理由：一旦页面能传锚点清单，"每页各写一份"就从后门回来了
 *   （页面必须知道阈值才对得上 ⇒ 规则又分叉了）。
 */

import { useEffect, useState } from 'react';
import { Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { TOKENS_LIGHT } from '../theme/tokens.generated.js';

/* ───────────────────────── 阈值常量（照抄原型 · 导出以便 spec 对字） ───────────────────────── */

/** 块高下限：太矮不值得做锚点（**显式 `data-anchor` 不受此限**） */
export const MIN_BLOCK = 240;
/** 页面/视口 比下限：不到 1.5 屏不算"长页面"（`AC-O2.36` 原文「≥ 1.5 屏」） */
export const MIN_RATIO = 1.5;
/** 下探时"大子块"的高度门槛 */
export const MIN_CHILD = 200;
/** 块进入候选的最低高度（原型 `ch.offsetHeight < 120` 那条） */
export const MIN_TOP = 120;
/** 父块"本身很高"的下探判据（原型 `> 700`） */
export const TALL_PARENT = 700;
/** 兜底探测（`section.card`）最多取几个 */
export const DEEP_MAX = 6;
/** 文案截断长度（原型 `slice(0, 14)`） */
export const LABEL_MAX = 14;
/** 滚动同步的偏移（原型 `scrollY + 140`）—— 让"当前区块"在标题刚过顶栏时就切换 */
export const SYNC_OFFSET = 140;
/** 复测时点（原型：`load` 后 240ms + 固定 900ms）—— 字体/图片就绪后版面会变 */
export const RECHECK_DELAYS: readonly number[] = [240, 900];

/* ───────────────────────── 纯逻辑层（零 DOM · 可直测） ───────────────────────── */

/**
 * 一个候选块的**测量结果** —— 纯逻辑层唯一的输入形状。
 *
 * 🔴 刻意**只装数字与布尔**，不装 HTMLElement：
 * ① 纯函数因此可在 node 环境直测（不需要 DOM）；
 * ② `element` 单独作为 `key`（用作 React `key` 与去重标识），类型放宽到 `unknown`
 *    是为了让 spec 能传字符串占位 —— **不要在逻辑里读它的属性**。
 */
export interface BlockMeasure {
  /** 元素标识（真实实现传 HTMLElement；spec 传字符串）—— 只作 key / 去重，不被读取 */
  readonly key: unknown;
  /** 块高度（`offsetHeight`） */
  readonly height: number;
  /** 该块内「卡片 / 网格」类大子块的**高度列表**（无则空数组） */
  readonly innerHeights: readonly number[];
  /** 该块是否有显式 `data-anchor`（有则不受 `MIN_BLOCK` 限制） */
  readonly hasDataAnchor: boolean;
  /** 已解析出的文案；`null` = 无标题 ⇒ **不产出锚点**（不显示「区块 N」占位） */
  readonly label: string | null;
  /**
   * 是否可参与导航（原型 `usable()` 的结论）：
   * 排除 `data-no-anchor` / `.spec-note` / `.kbd-hint` / `display:none`。
   * ⇒ **在采集层就判好**：纯逻辑层不需要知道 CSS 类名（那是 DOM 细节）。
   */
  readonly usable: boolean;
}

/** 锚点清单的一项（纯逻辑层的输出） */
export interface AnchorItem<K = unknown> {
  readonly key: K;
  readonly label: string;
}

/** 判定结果 —— `null` 表示**不产出**（短页 / 候选不足） */
export interface AnchorPlan<K = unknown> {
  readonly items: readonly AnchorItem<K>[];
  /** 页面/视口比（诊断用；spec 断言阈值时读它） */
  readonly ratio: number;
}

/**
 * 一个块**是否应该下探到它的子块**（原型那段 `inner.length >= 2 && (...)`）。
 *
 * 抽出来单独导出，是因为这条判据是**最容易漂移的一条**：
 * 它有两个"或"条件，只看其中一条就会在某些页面上多出一堆碎锚点。
 *
 * @param innerCount 该块内「卡片 / 网格」类**大子块**的个数（原型 `inner.length`）
 * @param everyChildTitled 这些大子块是否**每一个都有自己的一级标题**（原型 `inner.every(hasOwnTitle)`）
 */
export function shouldDescend(
  block: Pick<BlockMeasure, 'height'>,
  innerCount: number,
  everyChildTitled: boolean,
): boolean {
  if (innerCount < 2) return false;
  // ① 父块本身很高（>700px）⇒ 它是个"内容容器"，下探拿更细的落点
  // ② 每个大子块**都有自己的标题** ⇒ 这是一组并列卡片，父块只是排版容器
  return block.height > TALL_PARENT || everyChildTitled;
}

/**
 * 🔴 **核心纯函数：从测量结果推导锚点清单**（原型 `buildAnchorBar()` 的规则部分）。
 *
 * ## 输入为什么是「扁平清单」
 *
 * 原型在遍历顶层子块时**就地决定**下探，于是"父块退场、子块进场"这个动作
 * 混在循环里，依赖真 DOM 的 `children` 与 `contains()`。
 * 本函数改成分工：**采集层（`collect()`）负责"下探 / 父块退场"**，
 * 出来的一条一维 `blocks` 里**已经没有"包含其它锚点的父容器"**了
 * （父块被它的子块取代，正是原型最后那句去重想达到的效果）。
 * ⇒ 纯逻辑层因此只需三条规则（见下表），且**零 DOM**、可在 node 环境直测。
 *
 * ## 与原型逐条对照
 *
 * | 原型步骤 | 本函数 |
 * |---|---|
 * | `usable()` 过滤 + `offsetHeight < 120` 跳过 | 采集层已过滤，本函数**再判一次**（防御：调用方可能直传） |
 * | 下探条件 `≥2 大子块 && (父>700 或 子块各有标题)` | `collect()` 里用 `shouldDescend()` 做完 |
 * | `consider()`：有 label 才收；**非显式**时 `< MIN_BLOCK` 丢弃 | 同（`hasDataAnchor` 豁免 `MIN_BLOCK`） |
 * | `items.length < 2` 时兜底下探 `section.card` | `deepCandidates` 参数（采集层已按同规则筛好） |
 * | 去掉「包含其它锚点」的父容器 | 已由采集层的"父块退场"实现 |
 * | 按文档顺序排序 | 输入即文档顺序（采集层按 `content.children` 走），输出保序 |
 * | 阈值闸 `length < 2 \|\| ratio < MIN_RATIO` ⇒ `return` | 同（**返回 `null`**，语义比 `return` 明确） |
 *
 * ⚠️ 返回 `null` 而不是空数组：调用方必须区分"没规则命中"与"我还没测量"，
 * 两者都不渲染 —— 但后者（首帧 / SSR）不该被误读成"这页判定为短页"。
 */
export function pickAnchors(
  blocks: readonly BlockMeasure[],
  deepCandidates: readonly BlockMeasure[],
  opts: { readonly viewportHeight: number; readonly pageHeight: number },
): AnchorPlan | null {
  const items: AnchorItem[] = [];
  const seen = new Set<unknown>();
  for (const b of [...blocks, ...deepCandidates]) {
    if (!b.usable || b.label === null) continue;
    if (!b.hasDataAnchor && b.height < MIN_BLOCK) continue;
    if (seen.has(b.key)) continue;
    seen.add(b.key);
    items.push({ key: b.key, label: b.label });
  }

  const ratio = opts.pageHeight / Math.max(1, opts.viewportHeight);
  if (items.length < 2 || ratio < MIN_RATIO) return null;
  return { items, ratio };
}

/* ───────────────────────── 文案解析 ───────────────────────── */

/**
 * 块内标题的选择器 —— 见下方 `labelOf()` 的说明（为什么比原型宽）。
 *
 * 🔴 **导出**：spec 要断言它覆盖 `h2`–`h6`（这正是那个真 bug 的防回退闸，见 spec §④.5）。
 */
export const TITLE_SELECTOR = '.card-head h3, .card-head h2, h2, h3, h4, h5, h6, .sec-head b, .q-title';

/**
 * 取锚点文案 —— 优先级：`data-anchor` → 块内首个标题 → `null`。
 *
 * 🔴 **无标题必须返回 `null`，不能返回「区块 N」**（`AC-O2.36` 明写：
 * 「无标题则不产出（不显示「区块 N」占位）」）。原型注释里的"最后回落「区块 N」"
 * 是**它自己的注释与实现不一致**（实现 `return t ? t.slice(0,14) : null` 并不回落）——
 * 以 **AC 正文 + 实现**为准，注释那句不移植。
 *
 * ## 🔴 为什么选择器比原型**宽**（`h2`–`h6` 全收）—— 2026-10-05 无头取证发现并修正
 *
 * 原型（`_chrome.js:248`）写的是 `.card-head h3, .card-head h2, h3, h2, .sec-head b, .q-title`，
 * 因为它的 HTML 是**手写的**：`.card-head` 里就是字面 `<h3>`。但 **React 侧没有 `.card-head`**，
 * 标题一律是 MUI `<Typography variant="subtitle1">` ⇒ **渲染成 `<h6>`**（不是 `h3`）。
 *
 * ⇒ 照抄原型选择器会让 `labelOf()` **对每个块都返回 `null`**（一个都匹配不上），
 * 于是候选永远不足 2 个、`pickAnchors()` 永远返回 `null`、**吸顶条永远不出现**。
 * 这个失效是**完全静默的**（不报错、不警告，只是"看起来页面就是没有导航条"）。
 *
 * 📌 无头取证实测（`_archive/anchor-probe/diag2.mjs`，视口 1440×380、页面 2.947 屏）：
 * 修前 `吸顶条=无`（该出却没出），修后 `吸顶条=有`。
 *
 * ⚠️ **不是"顺手放宽"**：`h2`–`h6` 是 MUI 语义标题的**全集**，多收 `h4~h6` 只是
 * 把"设计里 `.card-head` 内标题"这一事实在 React 侧等价表达出来。若将来页面用
 * `variant="h6"` 做**非标题**用途（装饰性大字），应给它加 `data-no-anchor`，
 * 而不是把选择器收窄回去（收窄会让所有 `subtitle1` 标题重新失配）。
 */
export function labelOf(el: Element): string | null {
  const explicit = el.getAttribute('data-anchor');
  if (explicit !== null && explicit.trim() !== '') return explicit.trim();
  const h = el.querySelector(TITLE_SELECTOR);
  const raw = h?.textContent ?? '';
  const t = raw.trim().replace(/\s+/g, ' ');
  return t === '' ? null : t.slice(0, LABEL_MAX);
}

/** 原型 `usable()` —— 排除整块 / 注释块 / 键盘提示 / 不可见 */
export function usable(el: Element): boolean {
  if (el.hasAttribute('data-no-anchor')) return false;
  if (el.classList.contains('spec-note') || el.classList.contains('kbd-hint')) return false;
  // ⚠️ `getComputedStyle` 在无头以外的环境（如 happy-dom 缺失）可能不存在 ⇒ 防御性读取
  const view = el.ownerDocument?.defaultView;
  if (view !== undefined && view !== null && view.getComputedStyle(el).display === 'none') return false;
  return true;
}

/* ───────────────────────── DOM 采集层（唯一的测量处） ───────────────────────── */

/** 原型那段 `/card|grid|bdg|wiz|ma-|rev-|rm-|tl/` 类名匹配 */
const INNER_CLASS_RE = /card|grid|bdg|wiz|ma-|rev-|rm-|tl/;

/** 把 DOM 采成纯逻辑层要的 `BlockMeasure` */
function measure(el: Element, key: unknown): BlockMeasure {
  const height = el.clientHeight;
  const inner = [...el.children].filter(
    (k) =>
      typeof k.className === 'string' &&
      INNER_CLASS_RE.test(k.className) &&
      usable(k) &&
      k.clientHeight > MIN_CHILD,
  );
  return {
    key,
    height,
    innerHeights: inner.map((k) => k.clientHeight),
    hasDataAnchor: el.hasAttribute('data-anchor'),
    label: labelOf(el),
    usable: usable(el),
  };
}

/**
 * 采集内容区顶层块，并把**该下探的父块替换成它的子块**（→ 扁平化，供 `pickAnchors`）。
 *
 * 这是 DOM 与纯逻辑之间**唯一**的适配点：所有"要不要下探""父块退场"的判定
 * 都在这里做完，纯逻辑层拿到的是一维清单。
 */
function collect(content: Element): { blocks: BlockMeasure[]; deep: BlockMeasure[] } {
  const blocks: BlockMeasure[] = [];

  for (const ch of [...content.children]) {
    const m = measure(ch, ch);
    if (!m.usable || m.height < MIN_TOP) continue;

    const bigChildren = m.innerHeights.length;
    // 原型要求「每个大子块都有自己的标题」—— 必须在真 DOM 里问子块
    // 🔴 选择器必须与 `labelOf()` 同源（`TITLE_SELECTOR`）：否则会出现
    //    「`labelOf` 认得出标题、这里却判成没标题」的分叉 ⇒ 下探判定与产物不一致。
    const everyChildTitled =
      bigChildren >= 2 &&
      [...ch.children]
        .filter((k) => typeof k.className === 'string' && INNER_CLASS_RE.test(k.className) && usable(k) && k.clientHeight > MIN_CHILD)
        .every((k) => k.querySelector(TITLE_SELECTOR) !== null);

    if (shouldDescend(m, bigChildren, everyChildTitled)) {
      // 下探：父块退场，子块各自成为锚点候选（= 原型最后的"去重"效果）
      for (const k of ch.children) {
        if (typeof k.className !== 'string' || !INNER_CLASS_RE.test(k.className)) continue;
        if (!usable(k) || k.clientHeight <= MIN_CHILD) continue;
        blocks.push(measure(k, k));
      }
    } else {
      blocks.push(m);
    }
  }

  // 兜底：顶层凑不出 2 个可导航块时（如长内容全在单一网格的列里），下探卡片级
  const navigable = blocks.filter((m) => m.usable && m.label !== null && (m.hasDataAnchor || m.height >= MIN_BLOCK));
  const deep =
    navigable.length < 2
      ? [...content.querySelectorAll('section.card, div.card')]
          .filter((el) => usable(el) && el.closest('[data-no-anchor]') === null && el.clientHeight >= MIN_BLOCK)
          .slice(0, DEEP_MAX)
          .map((el) => measure(el, el))
          .filter((m) => m.label !== null)
      : [];

  return { blocks, deep };
}

/* ───────────────────────── 组件 ───────────────────────── */

export interface AnchorBarProps {
  /**
   * 内容区滚动容器 —— 组件会**在里面找顶层块**。
   * `null` 表示还没挂上（首帧）⇒ 本次不测量，等下一轮 effect。
   */
  readonly contentRef: React.RefObject<HTMLElement | null>;
  /**
   * 关掉本页的吸顶条（原型 `initPage({ anchors:false })`）。
   * ⚠️ 用于**确实不该有**页内导航的页面（如全屏向导），而不是"懒得改"。
   */
  readonly disabled?: boolean;
}

/**
 * 吸顶页内导航（`AC-O2.36`）。
 *
 * ```tsx
 * const contentRef = useRef<HTMLDivElement>(null);
 * <Box ref={contentRef}>
 *   <AnchorBar contentRef={contentRef} />
 *   {/* 页面区块 *\/}
 * </Box>
 * ```
 *
 * ## 🔴 使用方式与原型的一处差异（有意为之）
 *
 * 原型是 `content.insertBefore(bar, content.firstChild)` —— **插到最前面**。
 * React 侧不能这么做（会与 React 管理的 DOM 打架），改为：
 * **调用方把 `<AnchorBar>` 写成内容区的第一个子节点**，效果等同。
 *
 * ## 🔴 三条使用纪律（否则本组件会数错块）
 *
 * 1. **`contentRef` 指向的必须是"内容区的直接容器"** —— 组件只遍历它的**直接子元素**
 *    （与原型 `content.children` 一致）。若指向页面根 `<Stack>`，那是对的；
 *    若指向某个卡片内部，就会数出走样。
 * 2. **块要有标题**（`h2` / `h3`）或显式 `data-anchor` —— 没标题的块**不产出锚点**
 *    （`AC-O2.36`：不显示「区块 N」占位）。⇒ 页面按 `Grid` / `Card` 正常摆标题即可，
 *    不需要为本组件做任何额外标记。
 * 3. **不想被导航到的块**加 `data-no-anchor`（如调试信息 / 键盘提示）。
 */
export function AnchorBar({ contentRef, disabled = false }: AnchorBarProps) {
  const tk = useTheme().jiaying.tokens;
  /**
   * 渲染态 —— **只存 `{id, label}`**，不存 DOM 元素。
   *
   * 🔴 为什么不存元素：`render` 阶段**只该读 state**。若把 `Element` 存进来，
   * 渲染时就要 `el.id`（读真实 DOM）⇒ 渲染变成**非幂等**的（同一份 state 在
   * 不同时刻渲染出不同结果），React 的并发/严格模式会暴露它。
   * `id` 在 effect 里生成并**回写到 DOM**，之后两者一致。
   */
  const [items, setItems] = useState<readonly { readonly id: string; readonly label: string }[]>([]);
  const [active, setActive] = useState(0);

  useEffect(() => {
    const content = contentRef.current;
    if (disabled || content === null) return;

    const doc = content.ownerDocument;
    const view = doc.defaultView;
    let disposed = false;
    /** 已登记的清理动作（每次测量重建；卸载/重跑时统一执行） */
    let cleanupSync: (() => void) | null = null;

    /**
     * 清掉上一轮给块加的 id / `data-jy-anchor`（避免重复测量后 id 越积越多）。
     * ⚠️ 只清自己加的那些 id（`jy-a` 前缀），用户写的 id 一律不动。
     */
    const clearMarks = (): void => {
      for (const el of content.querySelectorAll('[data-jy-anchor]')) {
        el.removeAttribute('data-jy-anchor');
        if (el.id.startsWith('jy-a')) el.removeAttribute('id');
      }
    };

    /**
     * 🔴 **一次完整测量** —— 可重复调用（这是本组件最要紧的一段）。
     *
     * ## 为什么必须能重复调用（2026-10-05 无头取证发现并修正的**真 bug**）
     *
     * 初版只在 `useEffect` 里跑**一次**。但本页的内容是**异步长出来的**：
     * 曲线走 `React.lazy` + `Suspense`（首帧只有 fallback）、最近记录等接口返回。
     * ⇒ effect 首次执行时页面还是"首屏高度"（实测 `scrollHeight === innerHeight`，
     * ratio = **1.0**）⇒ `pickAnchors()` 返回 `null` ⇒ **早早 `return`**，
     * 连 `RECHECK_DELAYS` 的复测定时器都没来得及注册。
     *
     * 等数据到位、页面长到 1.6~2.9 屏时，**没有任何东西会再测一次** ⇒
     * 「该出吸顶条的页面永远不出」——**完全静默**（不报错、不警告）。
     * 无头实测：视口 1440×380、页面 2.947 屏，修前 `吸顶条=无`。
     *
     * ## 修法（双保险，两者互补）
     * 1. **`ResizeObserver` 监听内容区高度变化** ⇒ 页面一长出来就复测（这是主路径）；
     * 2. **保留 `RECHECK_DELAYS` 定时复测** ⇒ 兜住 `ResizeObserver` 不可用的环境
     *    （以及字体加载后高度微调这类 observer 可能漏掉的时序）。
     *
     * 🔴 **复测必须能"双向"改变结论**：既能把条**撤掉**（原型的 recheck 语义：初次测量
     * 偏大、其实不到 1.5 屏），也能把条**装上**（本批修的：初次测量偏小、后来长够了）。
     * 只做前者 = 本 bug 的成因。
     */
    const measure = (): void => {
      if (disposed) return;
      cleanupSync?.();
      cleanupSync = null;

      clearMarks();
      const { blocks, deep } = collect(content);
      const plan = pickAnchors(blocks, deep, {
        viewportHeight: view?.innerHeight ?? 0,
        pageHeight: doc.body.scrollHeight,
      });

      if (plan === null) {
        // 短页 / 候选不足 ⇒ 不产出（可能是"还没长够"，由下一轮复测再判）
        setItems([]);
        return;
      }

      const els = plan.items.map((it) => it.key as Element);
      const rendered = els.map((el, i) => {
        if (el.id === '') el.id = `jy-a${String(i + 1)}`;
        el.setAttribute('data-jy-anchor', '');
        return { id: el.id, label: plan.items[i]?.label ?? '' };
      });
      setItems(rendered);

      const sync = (): void => {
        const y = (view?.scrollY ?? 0) + SYNC_OFFSET;
        let idx = 0;
        els.forEach((el, i) => {
          if (el.getBoundingClientRect().top + (view?.scrollY ?? 0) <= y) idx = i;
        });
        setActive(idx);
      };
      view?.addEventListener('scroll', sync, { passive: true });
      sync();
      cleanupSync = () => {
        view?.removeEventListener('scroll', sync);
      };
    };

    measure();

    /**
     * 主路径：内容区高度一变就复测（页面从"首屏高"长到"2 屏"会触发）。
     * ⚠️ 包一层 `setTimeout(0)` 防"ResizeObserver loop completed with undelivered
     *    notifications"警告（在回调里同步改 DOM 会触发它）。
     */
    let ro: ResizeObserver | null = null;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(() => {
        view?.setTimeout(measure, 0);
      });
      ro.observe(content);
    }

    /**
     * 🔴 **视口变化也要复测**（2026-10-05 无头取证发现的第二处修正）。
     *
     * 「≥ 1.5 屏」是**比值**判据 ⇒ **视口高变了，结论就变**。而 `ResizeObserver` 只看
     * **元素尺寸**（视口变化不改内容区高度，故不触发）。若只在 resize 时 `sync()`
     * （只更新高亮），就会出现：**同一份内容，缩小窗口该出条却不出、
     * 放大窗口该收条却仍留着**（实测 1440×900 时 ratio=1.304 本不该有，却残留着）。
     *
     * ⚠️ resize 很密集（拖动窗口）⇒ 用 `setTimeout(0)` 合流，避免每像素都重测。
     */
    const onResize = (): void => {
      view?.setTimeout(measure, 0);
    };
    view?.addEventListener('resize', onResize);

    /**
     * 🔴 **兜底复测**（原型 `recheck`）：字体 / 图片 / 懒加载块就绪后版面会变。
     * 与 `ResizeObserver` 互补 —— 后者的语义是"元素尺寸变了"，而这里还要兜住
     * "尺寸没变但内部块结构变了"（如 `Suspense` 从 fallback 换成真内容且高度恰好相同）。
     * ⚠️ 定时器用 `view.setTimeout`（与 `_chrome.js` 同源，清理时清的是同一个宿主）。
     */
    const timers = RECHECK_DELAYS.map((ms) => view?.setTimeout(measure, ms));

    return () => {
      disposed = true;
      ro?.disconnect();
      view?.removeEventListener('resize', onResize);
      cleanupSync?.();
      for (const t of timers) if (t !== undefined) view?.clearTimeout(t);
    };
  }, [contentRef, disabled]);

  if (items.length === 0) return null;

  return (
    <Box
      // `component="nav"` —— 语义与原型一致（`<nav aria-label="本页导航">`）
      component="nav"
      // 🔴 自带 `data-no-anchor`：本组件渲染在内容区内，采集时必须把自己排除
      data-no-anchor
      data-testid="anchor-bar"
      aria-label="本页导航"
      sx={{
        // 🔴 吸顶：钉在**顶栏下方**（`topbar-h` = 58px）—— `AC-O2.36` 原文「钉在顶栏下方」
        position: 'sticky',
        top: tk['topbar-h'],
        zIndex: 30,
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        py: '8px',
        // 原型 `margin: -6px 0 14px`（上收 6 抵消内容区 padding 的视觉缝）
        mt: '-6px',
        mb: '14px',
        // 横向可滚（锚点多时），但不显示滚动条（原型 `scrollbar-width: none`）
        overflowX: 'auto',
        scrollbarWidth: 'none',
        // 底部渐隐 —— 让下方内容"从条下浮出来"，而不是硬边
        background: `linear-gradient(${tk['c-bg']} 74%, transparent)`,
      }}
    >
      <Box
        component="span"
        sx={{
          flex: '0 0 auto',
          fontSize: tk['fs-11'],
          color: tk['c-text-3'],
          mr: '2px',
          whiteSpace: 'nowrap',
        }}
      >
        本页导航
      </Box>
      {items.map((it, i) => (
        <Box
          key={it.id}
          component="a"
          href={`#${it.id}`}
          // 🔴 高亮只表示"当前所在区块"，不是"已选中"（锚点不是 tab，无 ARIA 语义）
          data-active={i === active ? 'true' : undefined}
          onClick={() => {
            setActive(i);
          }}
          sx={{
            flex: '0 0 auto',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            px: '11px',
            py: '5px',
            borderRadius: tk['r-full'],
            border: '1px solid',
            borderColor: i === active ? tk['c-brand-line'] : tk['c-border'],
            bgcolor: i === active ? tk['c-brand-weak'] : tk['c-surface'],
            color: i === active ? tk['c-brand-fill'] : tk['c-text-2'],
            fontWeight: i === active ? tk['fw-600'] : 400,
            fontSize: tk['fs-12'],
            textDecoration: 'none',
            whiteSpace: 'nowrap',
            transition: `all ${tk['t-fast']}`,
            '&:hover': { borderColor: tk['c-brand-line'], color: 'text.primary' },
          }}
        >
          <Box
            component="span"
            sx={{
              fontFamily: tk['font-num'],
              fontSize: '10px',
              minWidth: 15,
              height: 15,
              px: '3px',
              borderRadius: tk['r-full'],
              display: 'grid',
              placeItems: 'center',
              // 🔴 序号徽底的"非激活"色取 `TOKENS_LIGHT`（`c-surface-sunk` 两套取值不同，
              //    但在**暗色主题下**该值只用于徽底，原型也未做主题分支 —— 一致性优先）
              bgcolor: i === active ? tk['c-brand'] : TOKENS_LIGHT['c-surface-sunk'],
              color: i === active ? tk['n-0'] : tk['c-text-3'],
            }}
          >
            {i + 1}
          </Box>
          {it.label}
        </Box>
      ))}
    </Box>
  );
}

export default AnchorBar;
